import {
  Editor as SlateEditor,
  Element as SlateElement,
  Point,
  Range,
  Transforms as SlateTransforms,
} from 'slate';

import { SLATE_TEXT_FORMATS } from 'src/lib/slate';

export const withLists = (editor) => {
  const { deleteBackward, normalizeNode } = editor;

  editor.normalizeNode = ([node, path]) => {
    if (SlateElement.isElement(node) && node.type === SLATE_TEXT_FORMATS.LIST_ITEM) {
      const children = node.children;
      const textNodes = [];
      const textPaths = [];

      // When a list-item has direct text children (or inline elements), collect them until you encounter a block
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        const childPath = path.concat(i);

        // If it's a text node or inline node (no block type), collect it
        if (!SlateElement.isElement(child) || !SlateEditor.isBlock(editor, child)) {
          textNodes.push(child);
          textPaths.push(childPath);
        } else if (textNodes.length > 0) {
          // Wrap the collected text nodes into a paragraph before continuing
          wrapTextNodesInParagraph(editor, textNodes, textPaths);
          return; // Important: normalize after each operation
        }
      }

      if (textNodes.length > 0) {
        wrapTextNodesInParagraph(editor, textNodes, textPaths);
        return;
      }
    }

    // Otherwise, use the original normalizer
    normalizeNode([node, path]);
  };

  editor.deleteBackward = (...args) => {
    const { selection } = editor;
    if (selection && Range.isCollapsed(selection)) {
      const [match] = SlateEditor.nodes(editor, {
        match: (n) =>
          !SlateEditor.isEditor(n) &&
          SlateElement.isElement(n) &&
          n.type === SLATE_TEXT_FORMATS.LIST_ITEM,
      });
      if (match) {
        const [block, path] = match;
        const start = SlateEditor.start(editor, path);
        if (Point.equals(selection.anchor, start) && block.type === SLATE_TEXT_FORMATS.LIST_ITEM) {
          SlateTransforms.setNodes(editor, { type: SLATE_TEXT_FORMATS.PARAGRAPH });
          SlateTransforms.unwrapNodes(editor, {
            match: (n) =>
              [SLATE_TEXT_FORMATS.BULLETED_LIST, SLATE_TEXT_FORMATS.NUMBERED_LIST].includes(n.type),
            split: true,
          });
          return;
        }
      }
    }

    deleteBackward(...args);
  };

  return editor;
};

function wrapTextNodesInParagraph(editor, nodes, paths) {
  const paragraph = {
    type: SLATE_TEXT_FORMATS.PARAGRAPH,
    children: nodes,
  };

  // Remove the text nodes first (in reverse order to avoid path issues)
  for (let i = paths.length - 1; i >= 0; i--) {
    SlateTransforms.removeNodes(editor, { at: paths[i] });
  }

  // Insert the new paragraph at the position of the first removed node
  const insertPath = paths[0].slice(0, -1);
  const insertIndex = paths[0][paths[0].length - 1];
  SlateTransforms.insertNodes(editor, paragraph, { at: insertPath.concat(insertIndex) });
}
